import type { ApiVendoDevice, ApiDeviceTransfer } from '@/api/vendo';

/**
 * Filters for device target-pickers (Apply rates to..., Link to data plan,
 * etc). Centralized so every picker applies the same "is this device a
 * valid target?" rules.
 *
 * Rules (locked 2026-05-28 with Ray):
 *   1. Operator currently owns it. Sold-but-still-visible audit rows
 *      (`isCurrentOwner === false`) are excluded.
 *   2. Setup wizard is done. Half-flashed / unconfigured devices have no
 *      working portal yet, so applying rates / linking plans to them is
 *      premature.
 *   3. No PENDING outgoing transfer. Once the seller accepts a transfer
 *      they shouldn't be modifying the device's rates / plan link from
 *      under the buyer.
 */

/**
 * True when the device is a stable, owned, no-transfer-pending target
 * suitable for surfacing in a picker.
 */
export function isPickableDevice(
   device: ApiVendoDevice,
   pendingTransferIds: Set<string>
): boolean {
   if (device.isCurrentOwner === false) return false;
   if (device.setupStatus !== 'done') return false;
   if (pendingTransferIds.has(device._id)) return false;
   return true;
}

/**
 * Build the device-id set isPickableDevice consults from a list of
 * outgoing transfers. Only counts `status: 'pending'` rows —
 * accepted/rejected/cancelled/expired don't block re-picking.
 *
 * Tolerates undefined input (React Query loading state) and both
 * populated-object and string-only `device` shapes the API returns.
 */
export function buildPendingTransferIds(
   transfers: ApiDeviceTransfer[] | undefined
): Set<string> {
   if (!transfers) return new Set();
   const ids = new Set<string>();
   for (const t of transfers) {
      if (t.status !== 'pending') continue;
      const id = typeof t.device === 'string' ? t.device : t.device?._id;
      if (id) ids.add(id);
   }
   return ids;
}
